import React, {ReactNode, useCallback, useMemo, useState, useRef} from 'react';
import {mcClose, mcInfoFilled, mcLocation, mcMoveLeft, mcSearch} from '@mc/react-ui-icons';
import {EnumTypVysledek, LayerVO, ServicesApi, SearchResultVO, EnumSearchTypes} from '../../../api';
import {useMapioContext} from '../../../mapioContext';
import {actionApiCall, WaitForQueryData} from '@mc/react-app-v4';
import {UseQueryResult} from 'react-query';
import {useLayersConfig} from '../../../hooks/useLayersConfig';
import {Coordinate, FeatureZoom, LayerId} from '@mc/react-mapio';
import {SelectBox, Button, Col, Icon, InverseBgProvider, Label, Modal, Row, Text} from '@mc/ui-v4';
import removeAccents from 'remove-accents';
import {isUserDbLayer} from '../../../helpers/LayerConfigUtils';
import {useAppConfig} from '@mc/react-app-base';
import {useIsMobile} from '../../../hooks/useIsMobile';
import {createCoordinateFeature, getSearchCoordinates} from '../../../helpers/SearchUtils';
import {buildLayerGroupNameMap, buildLegacySearchItems, buildSearchItems, SearchResultGroup} from '../../../helpers/SearchRanking';
import {useCatalogLayers} from '../../../hooks/useCatalogLayers';
import {useAtlasPristupnosti} from '../../../hooks/useTopicsMap';
import {LegendModal} from '../../modals/LegendModal';
import {ApiCallWaitingModal} from '../../modals/ApiCallWaitingModal';
import {preloadLegendData} from '../../../helpers/LegendUtils';
import './Search.scss';

type SearchResult = SearchResultVO & {title?: string; typVysledek?: EnumTypVysledek; statusRow?: boolean; count?: number};

/** Prodleva automatického hledání po dopsání (ms). */
const SEARCH_DEBOUNCE_MS = 450;

/** Vrátí popis počtu nalezených objektů se správným skloňováním. */
const getFoundCountLabel = (count: number): string => {
    if (count === 1) return 'Nalezen 1 objekt';
    if (count >= 2 && count <= 4) return `Nalezeny ${count} objekty`;
    return `Nalezeno ${count} objektů`;
};

type Props = Record<string, never>;

export const Search: React.FC<Props> = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [searchText, setSearchText] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchedValue, setSearchedValue] = useState<SearchResultVO>(null);
    const [searchItems, setSearchItems] = useState<SearchResult[]>([]);
    // ID vrstev, ve kterých byly nalezeny výsledky – pro otevření legendy ze stavového řádku.
    const [resultLayerIds, setResultLayerIds] = useState<number[]>([]);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeSearchIdRef = useRef<number>(0);
    const mapioContext = useMapioContext();
    const {mapRef, disabled, context, lang, topicId, activeLayers, topic} = mapioContext.value;
    const {setSelectedDetailFeature} = mapioContext;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    // Nové chování našeptávače (MCW-29537) je podmíněné příznakem organizace „Atlas přístupnosti“ (MCW-29581).
    // Bez příznaku zůstává hledání přesně v původní podobě – průběžné vykreslování, pořadí ze serveru,
    // bez omezení počtu výsledků na skupinu.
    const atlasPristupnosti: boolean = useAtlasPristupnosti();
    // Katalog vrstev – z něj bereme název nadřazené (hlavní) skupiny, pod kterou se sloučí výsledky dílčích vrstev.
    // Slučuje se JEN ve zjednodušeném public zobrazení (MCW-29537); v běžném našeptávači zůstávají skupiny
    // po jednotlivých vrstvách/kategoriích, jak požaduje MCW-26078.
    const catalogLayersQuery = useCatalogLayers(topicId);
    const layerGroupNames: Map<number, string> = useMemo(
        () => (atlasPristupnosti && topic?.publicSimpleView ? buildLayerGroupNameMap(catalogLayersQuery.data) : undefined),
        [catalogLayersQuery.data, topic?.publicSimpleView, atlasPristupnosti],
    );
    // @ts-ignore
    const {serverPublic, serverContextPath} = useAppConfig();
    const isMobile = useIsMobile();
    const getSearchableLayerList: string = useMemo(() => {
        let searchableList = null;
        if (layersConfigQuery.data) {
            searchableList = Object.values(layersConfigQuery.data)
                .filter((item: LayerVO) => item.searchable && activeLayers.includes(item.id as LayerId))
                .map((item: LayerVO) => item.id)
                .join(',');
        }
        return searchableList;
    }, [layersConfigQuery.data, activeLayers]);

    /** Všechny searchable vrstvy (bez ohledu na activeLayers) — pro vyhledávání typu LAYERS */
    const getAllSearchableLayerList: string = useMemo(() => {
        if (!layersConfigQuery.data) return null;
        return Object.values(layersConfigQuery.data)
            .filter((item: LayerVO) => item.searchable)
            .map((item: LayerVO) => item.id)
            .join(',');
    }, [layersConfigQuery.data]);

    const highlightSearchText = useCallback((text: string, searchText: string): ReactNode => {
        if (!text || !searchText) return text;

        const searchPhrases: string[] = removeAccents(searchText)?.toLowerCase().split(' ') || [];
        const splittedText: string[] =
            removeAccents(text)
                ?.toLowerCase()
                .split(new RegExp('(' + searchPhrases.join('|') + ')', 'gi')) || [];
        let start: number = 0;

        return splittedText.map((item: string, index: number): ReactNode => {
            const end: number = start + item.length;
            const textPart: string = text.substring(start, end);
            start = end;

            return searchPhrases.includes(item.toLowerCase()) || item === ' ' ? (
                <span className={'highlight'} key={`${index}-${item}`}>
                    {textPart}
                </span>
            ) : (
                textPart
            );
        });
    }, []);

    function handleFeaturePreview(value: SearchResult, select?: boolean) {
        if (value.typVysledek === EnumTypVysledek.LAYER && !value.attrs?.featureId) return;
        if (value.attrs?.featureId) {
            const featurePointer = {
                featureId: value.attrs.featureId,
                layerId: value.attrs.layer,
            };
            if (select) {
                // Klik na položku → vybrat objekt a zobrazit jeho detail
                mapRef?.current?.selectFeatures([featurePointer], {zoom: FeatureZoom.CENTER});
                setSelectedDetailFeature([featurePointer]);
            } else {
                // Najetí myší → pouze zacentrovat na objekt v mapě, detail NEzobrazovat.
                // Záměrně nepoužíváme selectFeatures – ten vyvolá SELECT event, který v MapPage
                // nastaví selectedDetailFeature (otevře detail) a při otevřené atributové tabulce
                // navíc spustí synchronizaci výběru (setCheckedIds → handleMove → moveToFeatureHandler),
                // což by mohlo vést k zacyklení.
                mapRef?.current?.centerOnFeatures([featurePointer]);
            }
        } else {
            mapRef?.current?.activateObjectPreview(true, value);
            mapRef?.current?.setLocation(value.attrs?.position as Coordinate);
        }
    }

    const handleClearSearch = useCallback((): void => {
        // Zneplatní rozběhnuté requesty, aby jejich odpovědi neovlivnily UI po vymazání.
        activeSearchIdRef.current++;
        setIsSearching(false);
        if (searchedValue?.id) {
            mapRef?.current?.unloadFeatures([searchedValue.id]);
        }
        mapRef?.current?.unloadFeatures(['coordinate-result']);
        mapRef?.current?.activateObjectPreview(false);
        mapRef?.current?.selectFeatures([]);
        setSelectedDetailFeature([]);
        setSearchedValue(null);
        setSearchText('');
        setSearchItems([]);
        setResultLayerIds([]);
    }, [searchedValue]);

    const handleTextChange = useCallback(
        (text: string): void => {
            setSearchText(text);
            // Nový vstup zneplatní předchozí requesty.
            activeSearchIdRef.current++;

            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current);
            }

            if (!text || text.length < 3) {
                setSearchItems([]);
                setIsSearching(false);
                return;
            }

            debounceRef.current = setTimeout(() => {
                debounceRef.current = null;

                // Zkusíme nejprve rozpoznat souřadnice
                const coordinates = getSearchCoordinates(text);
                if (coordinates) {
                    // merge: false (výchozí) v loadFeatures evidentně nefunguje, proto nejprve volám unloadFeatures
                    mapRef?.current?.unloadFeatures(['coordinate-result']);
                    mapRef?.current?.loadFeatures([createCoordinateFeature(coordinates)], {zoom: FeatureZoom.CENTER_AND_ZOOM});
                    setSearchItems([]);
                    setIsSearching(false);
                    return;
                }

                // Každé nové hledání dostane unikátní ID – stará odpověď se ignoruje
                const searchId = ++activeSearchIdRef.current;
                setIsSearching(true);
                setSearchItems([]);

                // S atlasem přístupnosti se výsledky ze všech paralelních requestů sbírají a zobrazí se až
                // najednou, po dokončení posledního z nich (do té doby se v našeptávači zobrazuje
                // „Probíhá hledání…“) – MCW-29537. Bez atlasu se vykresluje průběžně jako dřív.
                const collectedGroups = new Map<string, SearchResultGroup<SearchResult>>();
                // Deduplikace features napříč paralelními requesty
                const seenFeatureKeys = new Set<string>();
                const types = Object.values(EnumSearchTypes);
                let completedCount = 0;

                const showCollectedResults = (): void => {
                    const {items, foundCount, layerIds} = atlasPristupnosti
                        ? buildSearchItems(Array.from(collectedGroups.values()), {
                              locationsFirst: topic?.showLocationsFirst,
                              layerGroupNames,
                              // Limit na skupinu neuvádíme – použije se výchozích 10, stejně jako ořezává server.
                          })
                        : buildLegacySearchItems(Array.from(collectedGroups.values()), {locationsFirst: topic?.showLocationsFirst});
                    setResultLayerIds(layerIds);
                    // Stavový řádek (počet + ikona legendy) přidáme jako první, nevybíratelnou položku seznamu.
                    setSearchItems(foundCount > 0 ? [{statusRow: true, count: foundCount} as SearchResult, ...items] : items);
                };

                types.forEach(type => {
                    // Pro LAYERS hledáme ve všech searchable vrstvách, pro ostatní typy jen v aktivních
                    const featuresList = type === EnumSearchTypes.LAYERS ? getAllSearchableLayerList : getSearchableLayerList;
                    ServicesApi.searchLayer(topicId, {
                        searchText: text,
                        type: type,
                        context: context,
                        features: featuresList,
                    })
                        .then(response => {
                            // Ignorujeme odpovědi ze starých hledání
                            if (searchId !== activeSearchIdRef.current) return;
                            const data = response.data;

                            Object.keys(data.layerInfo ?? {}).forEach((key: string): void => {
                                const layerInfo = data.layerInfo?.[key];
                                const layerIdNum = Number(key) as LayerId;

                                const newFeatures: SearchResult[] = (data.results?.[key] ?? [])
                                    .filter((item: SearchResultVO): boolean => {
                                        // Pokud je LAYER výsledek (bez featureId) a vrstva je aktivní → nezobrazujeme
                                        if (layerInfo?.typVysledek === EnumTypVysledek.LAYER && !item.attrs?.featureId && activeLayers.includes(layerIdNum)) {
                                            return false;
                                        }
                                        const featureKey = `${key}:${item.attrs?.featureId ?? item.id}`;
                                        if (seenFeatureKeys.has(featureKey)) return false;
                                        seenFeatureKeys.add(featureKey);
                                        return true;
                                    })
                                    .map((item: SearchResultVO): SearchResult => ({typVysledek: layerInfo?.typVysledek, ...item}));

                                if (newFeatures.length === 0) return;

                                const group = collectedGroups.get(key);
                                if (group) {
                                    group.items.push(...newFeatures);
                                } else {
                                    collectedGroups.set(key, {key, name: layerInfo?.nazev, typVysledek: layerInfo?.typVysledek, items: newFeatures});
                                }
                            });

                            // Bez atlasu přístupnosti se výsledky přidávají do seznamu průběžně, jak dobíhají.
                            if (!atlasPristupnosti) {
                                showCollectedResults();
                            }
                        })
                        .catch(() => {
                            /* ignorujeme chyby dílčích requestů */
                        })
                        .finally(() => {
                            if (searchId !== activeSearchIdRef.current) return;
                            completedCount++;
                            if (completedCount === types.length) {
                                // S atlasem přístupnosti se teprve teď (po dokončení všech dílčích dotazů)
                                // výsledky seskupí, seřadí podle relevance a zobrazí.
                                if (atlasPristupnosti) {
                                    showCollectedResults();
                                }
                                setIsSearching(false);
                            }
                        });
                });
            }, SEARCH_DEBOUNCE_MS);
        },
        [getSearchableLayerList, getAllSearchableLayerList, topicId, context, activeLayers, topic?.showLocationsFirst, layerGroupNames, atlasPristupnosti],
    );

    const handleChange = useCallback(
        (value: SearchResult): void => {
            if (!value) return;

            // setSearchText(value?.attrs?.label ?? searchText);
            setSearchedValue(value);

            const layerId = value.attrs?.layer as LayerId;

            // Pokud je výsledek typu LAYER (featureId === null) a vrstva je vypnutá → zapnout vrstvu
            if (value.typVysledek === EnumTypVysledek.LAYER && !value.attrs?.featureId && !activeLayers.includes(layerId)) {
                const newLayers = [...activeLayers, layerId];
                mapioContext.setActiveLayers(newLayers);
                mapRef?.current?.setLayers(newLayers);
                mapRef?.current?.zoomOnLayer(layerId, {padding: [100, 100, 300, 100]});
                return;
            }

            // Pokud je výsledek typu LAYER (featureId === null), vrstva je user_db,
            // není serverPublic a vrstva je aktivní → otevřeme atributovou tabulku
            if (
                !serverPublic &&
                value.attrs?.featureId === null &&
                value.typVysledek === EnumTypVysledek.LAYER &&
                isUserDbLayer(layersConfigQuery.data, layerId) &&
                activeLayers.includes(layerId)
            ) {
                // Nic se nestane dokud to zas nebude chtít někdo odkomentovat :)))))))
                // mapioContext.openAttributeTable(layerId);
            } else {
                handleFeaturePreview(value, true);
            }
        },
        [searchText, serverPublic, layersConfigQuery.data, activeLayers, mapRef],
    );

    const handleItemMouseEnter = useCallback(
        (item: SearchResult): void => {
            if (item.title || item.statusRow) {
                return;
            }
            handleFeaturePreview(item);
        },
        [mapRef],
    );

    const handleItemMouseLeave = useCallback((): void => {
        // Vypneme jen náhled souřadnic/objektu. Výběr záměrně nerušíme – hover už nic nevybírá,
        // takže selectFeatures([]) by jen zbytečně zrušil detail vybraný kliknutím (a vyvolal SELECT event).
        mapRef?.current?.activateObjectPreview(false);
    }, [mapRef]);

    const handleOpenLegend = useCallback(async (): Promise<void> => {
        const legendLayerIds = (resultLayerIds.length > 0 ? resultLayerIds : activeLayers) as LayerId[];
        // Přednačteme všechna data legendy (atributy pro vyhledávání, konfiguraci vrstev i legendy WMS/WMTS vrstev)
        // jedním Promise.all, aby se legenda ze stavového řádku otevřela rovnou s daty.
        const {searchableAttributes, layersConfig, layerLegends} = await Modal.showWaiting(
            actionApiCall(async () => await preloadLegendData(legendLayerIds, lang, context, layersConfigQuery.data)),
            () => <ApiCallWaitingModal className={'z-9999'} onClose={undefined} />,
        );

        Modal.show(({onClose}) => (
            <LegendModal
                className={'z-9999'}
                onClose={onClose}
                layerIds={legendLayerIds}
                searchableAttributes={searchableAttributes}
                layersConfig={layersConfig}
                layerLegends={layerLegends}
                formattedLegend={topic?.formatovanaLegenda}
            />
        ));
    }, [resultLayerIds, activeLayers, lang, context, layersConfigQuery.data, topic]);

    const renderSearchItem = useCallback(
        (item: SearchResult): ReactNode => {
            if (item.statusRow) {
                return (
                    <Row className={'pl-1 py-0 pr-0 search-status-row align-items-center'} noGutters>
                        <Col>
                            <Text italic intent={'secondary'}>
                                {getFoundCountLabel(item.count ?? 0)}
                            </Text>
                        </Col>
                        <Col xs={'auto'}>
                            <Button
                                style={'link'}
                                noFocus
                                icon={<Icon intent={'primary'} icon={mcInfoFilled} />}
                                onClick={async (e: React.MouseEvent) => {
                                    e?.stopPropagation();
                                    await handleOpenLegend();
                                }}
                            />
                        </Col>
                    </Row>
                );
            }
            if (item.title) {
                return <Label className={'py-1 px-1'}>{item.title}</Label>;
            }

            const {label, labelSuffix, layer} = item.attrs;
            const layersConfig: LayerVO = layersConfigQuery?.data[layer];

            return (
                <Row className={'px-1'} noGutters>
                    <Col className={'pr-1'} xs={'auto'}>
                        <div className={'align-items-center d-flex inverse-background justify-content-center'}>
                            {item.typVysledek === EnumTypVysledek.LOCATION ? (
                                <InverseBgProvider inverse>
                                    <div
                                        className={'align-items-center d-flex inverse-background justify-content-center rounded-circle'}
                                        style={{backgroundColor: '#294172'}}
                                    >
                                        <Icon icon={mcLocation} size={'sm'} />
                                    </div>
                                </InverseBgProvider>
                            ) : (
                                <>
                                    {(layersConfig?.layerImage ?? layersConfig?.style?.marker) && (
                                        <img
                                            className={'icon-image'}
                                            src={serverContextPath + '/api/files/img/' + (layersConfig?.layerImage ?? layersConfig?.style?.marker)}
                                            alt={layersConfig?.label}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </Col>
                    <Col>
                        <Text type={'listItem'}>{highlightSearchText(label, searchText)}</Text>
                        {labelSuffix && (
                            <>
                                <Text intent={'secondary'}> {'\u25CF'} </Text>
                                {highlightSearchText(labelSuffix, searchText)}
                            </>
                        )}
                    </Col>
                </Row>
            );
        },
        [layersConfigQuery.data, searchText, highlightSearchText, serverContextPath, handleOpenLegend],
    );

    return (
        <WaitForQueryData queries={[layersConfigQuery]} inline>
            {() => (
                <div
                    className={`search-bar-wrapper drop-shadow ${isSearching ? 'searching' : ''} ${isOpen ? 'position-absolute' : ''} ${isMenuOpen ? 'active' : ''} ${isMobile ? 'mobile' : ''} ${
                        serverPublic ? 'public' : ''
                    }`}
                >
                    {!isOpen && isMobile ? (
                        <Button
                            className={`drop-shadow ${isOpen ? 'active' : ''}`}
                            style={'link'}
                            noFocus
                            disabled={disabled}
                            dropdown
                            onClick={() => setIsOpen(!isOpen)}
                            icon={<Icon intent={'primary'} icon={mcSearch} />}
                        />
                    ) : (
                        <div
                            className={`${isMobile ? 'ui-v4-button-group' : ''}`}
                            aria-autocomplete="none"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                        >
                            {isMobile && (
                                <Button
                                    className={`z-999 ${isOpen ? 'active' : ''}`}
                                    style={'link'}
                                    noFocus
                                    disabled={disabled}
                                    dropdown
                                    onClick={() => setIsOpen(!isOpen)}
                                    icon={<Icon intent={'primary'} icon={mcMoveLeft} />}
                                />
                            )}
                            <SelectBox
                                auto-complete={'none'}
                                allowSelectItem={(_id: string, item: SearchResult): boolean => !item?.title && !item?.statusRow}
                                // @ts-ignore
                                showMenuOnFocus
                                disabled={disabled}
                                placeholder={'Hledat adresu, parcelu, objekt...'}
                                className={`search ${isMobile && serverPublic && 'public'}`}
                                dropDownIconClickOpenMenu
                                hasDropdownIcon={searchItems.length > 0 || isSearching}
                                noItemsFound={
                                    isSearching
                                        ? atlasPristupnosti
                                            ? 'Probíhá hledání…'
                                            : 'Vyhledávám...'
                                        : 'Pro zadaný řetězec nebyly nalezeny žádné záznamy'
                                }
                                getItemName={() => searchText}
                                addonAfterCompact
                                addonAfter={
                                    searchedValue && (
                                        <Button
                                            style={`${isMobile && serverPublic ? 'link' : 'primary'}`}
                                            icon={<Icon fixedWidth icon={mcClose} />}
                                            onClick={handleClearSearch}
                                        />
                                    )
                                }
                                items={searchItems}
                                value={searchText as SearchResult}
                                onTextChange={handleTextChange}
                                onChange={handleChange}
                                onOpenMenu={(): void => setIsMenuOpen(true)}
                                onCloseMenu={(): void => setIsMenuOpen(false)}
                                renderItemContent={(item: SearchResult): ReactNode => (
                                    <div onMouseEnter={() => handleItemMouseEnter(item)} onMouseLeave={handleItemMouseLeave} className={'active w-100'}>
                                        {renderSearchItem(item)}
                                    </div>
                                )}
                            />
                        </div>
                    )}
                </div>
            )}
        </WaitForQueryData>
    );
};
